import type { AxiosResponse } from 'axios';
import type { WithT } from 'i18next';
import { flow, includes, replace, trim } from 'lodash-es';
import type { ChangeEvent, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from '@mui/material';

import type { TargetUser } from 'common/accountActivationAndRecovery/types';
import { AuthActions } from 'common/auth/actions';
import { AUTH_STAGE } from 'common/auth/constants';
import type { AuthState } from 'common/auth/types';
import { Form } from 'common/component/ValidationFormAndField';
import type { RestError } from 'common/error/error_util';
import { CIError } from 'common/error/error_util';
import withTranslate from 'common/i18n/i18n';
import { CILogger } from 'common/log/log.provider';
import restClient from 'common/rest/client';
import { CISecondaryButton } from 'common/viewcomp/forms/buttons';
import { CITextField } from 'common/viewcomp/forms/fields/text';
import type { PageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { withPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import CIStepper from 'common/viewcomp/stepper/CIStepper';

import './activation_code.scss';

interface Props extends WithT {
    onCompleteStep: () => void;
    auth: AuthState;
    loginRequest: (username: string, temppass: string) => void;
    pageStatus: PageStatus;
    activeStep: number;
    onBackToLogin?: () => void;
    targetUser: TargetUser | null;
    isPasswordReset: boolean;
    prefilledActivationCode?: string;
    activationCodeReadOnly: boolean;
    showStepper: boolean;
}

interface CheckActivationCodeProps {
    user: {
        username: string;
        temppass: string;
        userId: string;
    };
    error: RestError;
    valid: boolean;
}

interface State {
    formSubmitted: boolean;
    isLoading: boolean;
}

const ActivationCodeFormComponent = (props: Props): ReactElement => {
    const {
        t,
        onCompleteStep,
        auth,
        loginRequest,
        pageStatus,
        activeStep,
        onBackToLogin,
        targetUser,
        isPasswordReset,
        prefilledActivationCode,
        activationCodeReadOnly,
        showStepper,
    } = props;
    const [state, setState] = useState<State>({
        formSubmitted: false,
        isLoading: false,
    });
    const activationCodeRef = useRef();

    const handleActivationCode = async ({ activationCode }): Promise<void> => {
        const activationCodeInput = escape(replace(activationCode, '-', ''));

        const userPk = targetUser?.value ?? null;

        pageStatus.dismiss();
        setState({
            ...state,
            formSubmitted: true,
            isLoading: true,
        });

        try {
            // The backend resolves the two identifier kinds on different query parameters.
            const activationUrl =
                targetUser?.type === 'username'
                    ? `/checkActivationCode?username=${userPk}&activationCode=${activationCodeInput}`
                    : `/checkActivationCode?userId=${userPk}&activationCode=${activationCodeInput}`;

            const loginDetails = await restClient
                .get(activationUrl)
                .then((res): AxiosResponse<CheckActivationCodeProps> => res);

            const { error, user } = loginDetails.data;
            if (!error && user) {
                loginRequest(user.username, user.temppass);
                onCompleteStep();
                CILogger.getInstance().info({
                    // kibana logging
                    message: 'Valid login.',
                    userPk: userPk,
                });
            } else {
                if (!error.code) {
                    pageStatus.error(t(CIError.fromRestError(error)?.translationKey));
                } else {
                    if (error.code === 'code_expired') {
                        pageStatus.error(t('activation.code_expired'));
                    } else if (error.code === 'invalid_code') {
                        pageStatus.error(t('activation.invalid_code'));
                    } else if (error.code === 'user_already_confirmed') {
                        pageStatus.error(t('activation.user_already_confirmed'));
                    } else if (error.code === 'tries_exceeded') {
                        (activationCodeReadOnly || isPasswordReset) &&
                            pageStatus.error(t('activation.regenerate_activation_code'));
                    }
                    CILogger.getInstance().info({
                        // kibana logging
                        message: 'Login attempt',
                        error: error.code,
                        userPk: userPk,
                    });
                }
                setState({
                    ...state,
                    formSubmitted: false,
                    isLoading: false,
                });
            }
        } catch (error) {
            setState((prevState) => ({ ...prevState, formSubmitted: false, isLoading: false }));
            pageStatus.error(t(CIError.fromRestError(error)?.translationKey));
            CILogger.getInstance().info({
                // kibana logging
                message: 'Login attempt',
                error: error,
                userPk: userPk,
            });
        }
    };

    // * Pre populate Activation code if this component is opened from Patient Chart page
    const activationCodeInitialValues = {
        activationCode: prefilledActivationCode ?? null,
    };
    useEffect((): void => {
        // valid code entered and cognito return new pass trigger, run onCompleteStep.
        if (auth.error && auth.error.trigger === AUTH_STAGE.NEW_PASS_REQUIRED) {
            onCompleteStep();
        } else if (auth.error) {
            // set cognitoError if error
            pageStatus.error(t(auth.error.translationKey));
            setState({
                ...state,
                formSubmitted: false,
                isLoading: false,
            });
        }
    }, [auth.error, onCompleteStep]);

    return (
        <div id="activation_code_view">
            <Form
                onSubmit={handleActivationCode}
                initialValues={activationCodeInitialValues}
                render={({ form, values, handleSubmit }): ReactElement => {
                    const disableButton = !values.activationCode || includes(values.activationCode, 'N');

                    return (
                        <div className="activation-code-container">
                            <div className="activation-header-text">
                                {isPasswordReset
                                    ? t('activation.patient_reset_header')
                                    : t('activation.activation_code_step')}
                            </div>
                            {showStepper && <CIStepper activeStep={activeStep} />}
                            {isPasswordReset && (
                                <div className="helper_text_patient">{t('activation.patient_information')}</div>
                            )}
                            {!isPasswordReset && (
                                <div className="activation-code-instructions">
                                    {t('activation.codeInstructionStart')}{' '}
                                    <strong>{t('activation.codeInstructionMid')}</strong>{' '}
                                    {t('activation.codeInstructionEnd')}
                                </div>
                            )}
                            <form id="activation_code_form" onSubmit={handleSubmit} noValidate>
                                <CITextField
                                    id="activationCode"
                                    name="activationCode"
                                    t={t}
                                    labelI18NKey=""
                                    className="code-text-field"
                                    placeholder="NNNN-NNNN"
                                    type="tel"
                                    margin="normal"
                                    fullWidth
                                    required
                                    maxLength={9}
                                    maskRegex="[0-9]{4}-[0-9]{4}"
                                    maskbdi
                                    inputRef={activationCodeRef}
                                    variant="outlined"
                                    maskOptions={{
                                        placeholder: 'N',
                                        clearMaskOnLostFocus: false,
                                        onBeforePaste: (pastedValue: string): void => {
                                            form.change('activationCode', trim(pastedValue));
                                        },
                                        onKeyDown: (event: ChangeEvent<HTMLInputElement>): void => {
                                            form.change('activationCode', trim(event.target.defaultValue));
                                        },
                                    }}
                                    disabled={activationCodeReadOnly}
                                />
                                <CISecondaryButton
                                    type="submit"
                                    id="ActivationCodeButton"
                                    className="code-button"
                                    disabled={disableButton}
                                    fullWidth
                                    isLoading={state.isLoading}
                                >
                                    {isPasswordReset ? t('activation.verify') : t('activation.create_account')}
                                </CISecondaryButton>
                            </form>
                            {onBackToLogin && (
                                <Link
                                    component="button"
                                    className="user-activation-back-to-login-link"
                                    onClick={onBackToLogin}
                                >
                                    {t('activation.back_to_login')}
                                </Link>
                            )}
                        </div>
                    );
                }}
            />
        </div>
    );
};

const mapStateToProps = ({ auth }: { auth: AuthState }) => ({ auth });

const mapDispatchToProps = { ...AuthActions };

const ActivationCodeForm = flow([withTranslate('user'), withPageStatus])(
    connect(mapStateToProps, mapDispatchToProps)(ActivationCodeFormComponent)
);

export default ActivationCodeForm;
